import { type DraftFile } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef } from 'react';

import { afterFrames } from '@/lib/animate';

import { FilePreview } from './FilePreview';

interface IFileGalleryProps {
  removeFile?: (file: DraftFile) => void;
  onRetryFile?: (file: DraftFile) => void;
  onSaveText?: (file: DraftFile, text: string) => void | Promise<void>;
  onInsertText?: (file: DraftFile, text: string) => void | Promise<void>;
  isPreviewEnabled?: boolean;
  attachedFiles: DraftFile[];
}

export const FileGallery = observer(
  ({
    attachedFiles,
    removeFile,
    onRetryFile,
    onSaveText,
    onInsertText,
    isPreviewEnabled,
  }: IFileGalleryProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!containerRef.current) return;
      afterFrames(5, () => {
        if (!containerRef.current) return;
        const scrollWidth = containerRef.current.scrollWidth;
        const clientWidth = containerRef.current.clientWidth;
        const maxScrollLeft = scrollWidth - clientWidth;

        containerRef.current.scrollTo({
          left: maxScrollLeft,
          top: 0,
          behavior: 'smooth',
        });
      });
    }, [attachedFiles.length]);

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{
          opacity: 1,
          scale: 1,
          transition: {
            type: 'spring',
            damping: 15,
          },
        }}
        exit={{
          opacity: 0,
          scale: 0.98,
          transition: {
            type: 'keyframes',
            duration: 0.25,
          },
        }}
      >
        <div ref={containerRef} className="overflow-x-auto scrollbar-hide">
          <motion.div
            layout
            transition={{
              layout: {
                type: 'spring',
                stiffness: 800,
                damping: 35,
                mass: 0.1,
              },
            }}
            className="p-1 flex items-center gap-3"
          >
            <AnimatePresence>
              {attachedFiles.map((file) => {
                return (
                  <FilePreview
                    key={file.localKey}
                    file={file}
                    removeFile={removeFile ? () => removeFile(file) : undefined}
                    onRetryFile={onRetryFile}
                    onSaveText={onSaveText}
                    onInsertText={onInsertText}
                    isPreviewEnabled={isPreviewEnabled ?? false}
                  />
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>
    );
  }
);
